import { Tooltip } from 'antd'
import { FC, ReactNode } from 'react'

/** Wraps a substituted value in a tooltip exposing its provenance. */
const ValueWithProvenance: FC<{
  value: ReactNode
  provenance: ReactNode
}> = ({ value, provenance }) => (
  <Tooltip title={provenance}>
    <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
      {value}
    </span>
  </Tooltip>
)

export const fmt = (n: number | null, precision = 2): string =>
  n === null ? '--' : n.toFixed(precision)

export interface MinThicknessRawInputs {
  P_MPa: number | null
  D_mm: number | null
  Sy_MPa: number | null
  E: number | null
  Y: number | null
  tsl_mm: number | null
  tNom_mm: number | null
}

export interface MinThicknessFormulaJsx {
  /** Symbolic + substituted JSX for each formula in the Min Thickness step. */
  tMinC: { symbolic: ReactNode; substituted: ReactNode }
  tMinL: { symbolic: ReactNode; substituted: ReactNode }
  mawpC: { symbolic: ReactNode; substituted: ReactNode }
  mawpL: { symbolic: ReactNode; substituted: ReactNode }
}

/**
 * Builds the symbolic + substituted JSX for every formula on the Min
 * Thickness step. Each substituted cell hovers to show input provenance.
 *
 * Kept in its own file to keep the step component focused on data wiring
 * and validation merging.
 */
export function buildMinThicknessFormulaJsx(
  raw: MinThicknessRawInputs
): MinThicknessFormulaJsx {
  const Pcell = (
    <ValueWithProvenance
      value={fmt(raw.P_MPa)}
      provenance='from Asset Model -- Design Pressure (P)'
    />
  )
  const Dcell = (
    <ValueWithProvenance
      value={fmt(raw.D_mm)}
      provenance='from Asset Model -- Outer Diameter (D = radius * 2)'
    />
  )
  const Sycell = (
    <ValueWithProvenance
      value={fmt(raw.Sy_MPa)}
      provenance='from Asset Model -- Yield Strength (S_y)'
    />
  )
  const Ecell = (
    <ValueWithProvenance
      value={fmt(raw.E)}
      provenance='from Asset Model -- Joint Efficiency (E)'
    />
  )
  const Ycell = (
    <ValueWithProvenance
      value={fmt(raw.Y)}
      provenance='from Asset Model -- Pressure Coefficient (Y)'
    />
  )
  const tslCell = (
    <ValueWithProvenance
      value={fmt(raw.tsl_mm)}
      provenance='from Asset Model -- Thickness for Supplemental Loads (t_sl)'
    />
  )
  const tNomCell = (
    <ValueWithProvenance
      value={fmt(raw.tNom_mm)}
      provenance='from Asset Model -- Nominal Wall Thickness (t_nom)'
    />
  )

  return {
    tMinC: {
      symbolic: (
        <>
          t<sub>min</sub>
          <sup>C</sup> = (P * D) / (2 * S<sub>y</sub> * E + 2 * P * Y)
        </>
      ),
      substituted: (
        <>
          ({Pcell} * {Dcell}) / (2 * {Sycell} * {Ecell} + 2 * {Pcell} * {Ycell})
        </>
      ),
    },
    tMinL: {
      symbolic: (
        <>
          t<sub>min</sub>
          <sup>L</sup> = (P * D) / (4 * S<sub>y</sub> * E + 4 * P * Y) + t
          <sub>sl</sub>
        </>
      ),
      substituted: (
        <>
          ({Pcell} * {Dcell}) / (4 * {Sycell} * {Ecell} + 4 * {Pcell} * {Ycell})
          + {tslCell}
        </>
      ),
    },
    mawpC: {
      symbolic: (
        <>
          MAWP<sup>C</sup> = (2 * S<sub>y</sub> * E * t<sub>nom</sub>) / (D - 2
          * Y * t<sub>nom</sub>)
        </>
      ),
      substituted: (
        <>
          (2 * {Sycell} * {Ecell} * {tNomCell}) / ({Dcell} - 2 * {Ycell} *{' '}
          {tNomCell})
        </>
      ),
    },
    mawpL: {
      symbolic: (
        <>
          MAWP<sup>L</sup> = (4 * S<sub>y</sub> * E * (t<sub>nom</sub> - t
          <sub>sl</sub>)) / (D - 4 * Y * (t<sub>nom</sub> - t<sub>sl</sub>))
        </>
      ),
      substituted: (
        <>
          (4 * {Sycell} * {Ecell} * ({tNomCell} - {tslCell})) / ({Dcell} - 4 *{' '}
          {Ycell} * ({tNomCell} - {tslCell}))
        </>
      ),
    },
  }
}
